import { RiMap2Line, Stack } from '@landler/component-library';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  LayerFactValue,
  MAP_LAYER_FACTS,
  MapRastersFact,
  R1FactType,
  ThreatType,
} from '@/api/rest/resources/types/fact';
import { WidgetSlot, WidgetSlotConfiguration, WidgetType } from '@/api/rest/resources/types/widget';
import { withSuspenseBoundary } from '@/components/SuspenseBoundary';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { DEFAULT_MAP_STYLE, LIGHT_MAP_STYLE } from '@/config/constants';
import { useIsFactInPurchasedIndicatorProducts } from '@/hooks/useIsFactInPurchasedIndicatorProducts';
import { useLandscapeView } from '@/hooks/useLandscapeView';
import { useMatchCurrentPage } from '@/hooks/useMatchCurrentPage';
import { usePathSet } from '@/hooks/usePathSet';
import { useProtectedAreasMethodology } from '@/hooks/useProtectedAreasMethodology';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';

import { usePicker } from '../Form/Picker';
import {
  HABITAT_INTACTNESS_THREAT_LAYER_OPTIONS,
  IBAT_LEGEND,
  LANDCOVER_VIEW_LAYERS,
  LANDSCAPE_VIEW_LAYERS,
  LAYER_OPTIONS,
  LayerProps,
  LEGEND_TYPE,
  LegendConfig,
  MAP_TYPE,
  SIEMENS_HACK_PROTECTED_AREAS_LAYER,
  SUBSCRIBED_LAYERS,
} from './constants';
import { HabitatIntactnessThreatLayers } from './HabitatIntactnessThreatLayers';
import { HabitatIntactnessThreatsPicker } from './HabitatIntactnessThreatsPicker';
import { useAddFactMapLayers } from './hooks/useAddFactMapLayer';
import {
  DEFAULT_LAYER_OPACITIES,
  DEFAULT_LAYER_OPACITY,
  useControlsContext,
  useMapSelectedLayer,
} from './hooks/useFormControls';
import { useIsSiemensOrganization } from './hooks/useIsSiemensOrganization';
import { DEFAULT_LEGEND_FILTER, LegendFilter, SetLegendFilter, useLegendFilter } from './hooks/useLegendFilter';
import { LayerEditMenu } from './LayerEditMenu/LayerEditMenu';
import { CategoryLegend, IndexLegend, Legend, LegendContainer, LegendItem } from './Legend';
import { GeojsonSourceLayer, RasterSourceLayer } from './MapLayer';

const YearPicker = () => {
  const { t } = useTranslation();
  const { setOpen } = usePicker();

  const controlsForm = useControlsContext();
  const selectedYear = controlsForm.watch('year');

  const selectedLayer = useMapSelectedLayer();
  const startYear = selectedLayer?.timeSeriesStart ? new Date(selectedLayer.timeSeriesStart).getFullYear() : 1950;
  const endYear = selectedLayer?.timeSeriesEnd ? new Date(selectedLayer.timeSeriesEnd).getFullYear() : 2030;
  const stepSize = selectedLayer?.timeSeriesStepSize === 'yearly' ? 1 : 10;
  const years = Array.from(
    { length: Math.floor((endYear - startYear) / stepSize) + 1 },
    (_, i) => startYear + i * stepSize,
  );

  const isHabitatIntactnessLayer = selectedLayer?.value === MAP_LAYER_FACTS.habitat_intactness_raster_urls;
  const handleOnValueChange = () => {
    controlsForm.setValue('year', selectedYear);
    setOpen(false);
  };

  const PICKER_SLOT_CONFIG: WidgetSlotConfiguration = {
    name: 'default_heat_map_layer_picker',
    type: WidgetType.o_map_layer,
    widgets: [
      {
        name: 'heat_map_layer_picker',
        type: WidgetType.o_form_picker,
        visible: true,
        relatedData: {},
        widgets: [
          {
            name: 'heat_map_layer_picker_trigger',
            type: WidgetType.o_form_picker_trigger,
            visible: true,
            relatedData: {
              label:
                selectedYear && selectedLayer?.isTimeSeries
                  ? selectedYear
                  : t('shared.projects.project.conservation.interactiveMap.labels.year'),
              disabled: !selectedLayer?.isTimeSeries,
            },
          },

          isHabitatIntactnessLayer
            ? {
                name: 'heat_map_layer_radio_group',
                type: WidgetType.o_c_form_radio_group_habitat_intactness_years,
                visible: true,
                relatedData: {
                  controlsForm,
                  name: 'year',
                  onValueChange: handleOnValueChange,
                },
              }
            : {
                name: 'heat_map_layer_picker_content',
                type: WidgetType.o_form_picker_content,
                visible: !!selectedLayer?.isTimeSeries,
                relatedData: {},
                widgets: [
                  {
                    name: 'heat_map_layer_radio_group',
                    type: WidgetType.o_form_radio_group,
                    visible: true,
                    relatedData: {
                      controlsForm,
                      name: 'year',
                      onValueChange: handleOnValueChange,
                    },
                    widgets: years.map(
                      (year): WidgetSlot => ({
                        name: `${year}`,
                        type: WidgetType.o_form_radio_item,
                        visible: true,
                        relatedData: { key: `${year}`, label: `${year}`, value: `${year}` },
                      }),
                    ),
                  },
                ],
              },
        ],
      },
    ],
  };

  return <WidgetSlotRenderer nestedWidget={PICKER_SLOT_CONFIG} />;
};

const PlotViewLayerPicker = ({
  layers,
  setLegendFilter,
}: {
  layers: LayerProps[];
  setLegendFilter: SetLegendFilter;
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { isActive: isLandscapeViewActive } = useLandscapeView();
  const { hasIbat, hasOsm } = useProtectedAreasMethodology();

  // TODO: BIG HACK - MVP-7252 - remove once layer is applicable to all organizations
  const isSiemensOrganization = useIsSiemensOrganization();

  let filteredLayers = layers.filter(
    (opt) =>
      !LANDSCAPE_VIEW_LAYERS.includes(opt.value as MAP_LAYER_FACTS) &&
      !LANDCOVER_VIEW_LAYERS.includes(opt.value as MAP_LAYER_FACTS) &&
      !SUBSCRIBED_LAYERS.includes(opt.value as MAP_LAYER_FACTS),
  );

  const hasLandCoverProduct = useIsFactInPurchasedIndicatorProducts(R1FactType.landcover);
  const hasThreatenedSpeciesProduct = useIsFactInPurchasedIndicatorProducts(
    MAP_LAYER_FACTS.species_presence_occurrence_location_geojson,
  );

  filteredLayers = useAddFactMapLayers(filteredLayers, SUBSCRIBED_LAYERS);

  if (!isLandscapeViewActive) {
    return <LayerPicker layers={filteredLayers} setLegendFilter={setLegendFilter} />;
  }

  if (hasLandCoverProduct) {
    const landCoverLayer = LAYER_OPTIONS.find((opt) => opt.value === MAP_LAYER_FACTS.dw_lulc);
    // TODO: BIG HACK - MVP-7252 - remove once layer is applicable to all organizations
    if (landCoverLayer && !isSiemensOrganization) {
      filteredLayers.push(landCoverLayer);
    }
  }

  if (hasThreatenedSpeciesProduct) {
    const threatenedSpeciesLayer = LAYER_OPTIONS.find(
      (opt) => opt.value === MAP_LAYER_FACTS.species_presence_occurrence_location_geojson,
    );
    if (threatenedSpeciesLayer) {
      filteredLayers.push(threatenedSpeciesLayer);
    }
  }

  if (hasIbat) {
    const ibatProtectedAreasLayer = LAYER_OPTIONS.find((opt) => opt.value === MAP_LAYER_FACTS.iucn_protected_areas);
    if (ibatProtectedAreasLayer) {
      filteredLayers.push(ibatProtectedAreasLayer);
    }

    // TODO: BIG HACK - MVP-7252 - remove once layer is applicable to all organizations
    if (isSiemensOrganization) {
      filteredLayers.push(SIEMENS_HACK_PROTECTED_AREAS_LAYER as LayerProps);
    }

    const combinedIbatLulcLayer = LAYER_OPTIONS.find((opt) => opt.value === MAP_LAYER_FACTS.combined_ibat_lulc_urls);
    if (combinedIbatLulcLayer && hasLandCoverProduct) {
      filteredLayers.push(combinedIbatLulcLayer);
    }
  }

  if (hasOsm) {
    const osmProtectedAreasLayer = LAYER_OPTIONS.find((opt) => opt.value === MAP_LAYER_FACTS.osm_protected_areas);
    if (osmProtectedAreasLayer) {
      filteredLayers.push(osmProtectedAreasLayer);
    }
    // TODO: BIG HACK - MVP-7252 - remove once layer is applicable to all organizations
    if (isSiemensOrganization) {
      filteredLayers.push(SIEMENS_HACK_PROTECTED_AREAS_LAYER as LayerProps);
      const combinedIbatLulcLayer = LAYER_OPTIONS.find((opt) => opt.value === MAP_LAYER_FACTS.combined_ibat_lulc_urls);
      if (combinedIbatLulcLayer) {
        filteredLayers.push(combinedIbatLulcLayer);
      }
    } else if (hasLandCoverProduct) {
      const combinedOsmLulcLayer = LAYER_OPTIONS.find((opt) => opt.value === MAP_LAYER_FACTS.combined_osm_lulc);
      if (combinedOsmLulcLayer) {
        filteredLayers.push(combinedOsmLulcLayer);
      }
    }
  }

  return <LayerPicker layers={filteredLayers} setLegendFilter={setLegendFilter} />;
};

const GeneralViewLayerPicker = ({
  layers,
  setLegendFilter,
}: {
  layers: LayerProps[];
  setLegendFilter: SetLegendFilter;
}) => {
  const filteredLayers = layers.filter(
    (opt) =>
      !LANDSCAPE_VIEW_LAYERS.includes(opt.value as MAP_LAYER_FACTS) &&
      !LANDCOVER_VIEW_LAYERS.includes(opt.value as MAP_LAYER_FACTS) &&
      !SUBSCRIBED_LAYERS.includes(opt.value as MAP_LAYER_FACTS),
  );

  return <LayerPicker layers={filteredLayers} setLegendFilter={setLegendFilter} />;
};

const useMapAvailableThreats = () => {
  const plotFacts = usePlotFacts<MapRastersFact>([MAP_LAYER_FACTS.habitat_intactness_raster_urls]);
  const rasterUrls = getFactFromFactArray<LayerFactValue>(
    plotFacts,
    MAP_LAYER_FACTS.habitat_intactness_raster_urls,
  )?.value;

  const availableThreats = rasterUrls ? (Object.keys(rasterUrls) as ThreatType[]) : [];

  return availableThreats.filter((t) => ThreatType?.[`${t}`] && t !== ThreatType.no_threat_area);
};

const useMapSelectedThreats = () => {
  const controlsForm = useControlsContext();
  return controlsForm.watch('habitatIntactnessThreats');
};

const HabitatIntactnessThreatLayersPicker = () => {
  const selectedLayer = useMapSelectedLayer();

  const threats = useMapAvailableThreats();
  const selectedThreats = useMapSelectedThreats();

  const controlsForm = useControlsContext();
  const setSelectedThreats = (newThreats: ThreatType[]) =>
    controlsForm.setValue('habitatIntactnessThreats', newThreats);

  if (selectedLayer?.value !== MAP_LAYER_FACTS.habitat_intactness_raster_urls) {
    return null;
  }

  return (
    <HabitatIntactnessThreatsPicker
      threats={threats}
      selectedThreats={selectedThreats}
      setSelectedThreats={setSelectedThreats}
    />
  );
};

const LayerPicker = ({ layers, setLegendFilter }: { layers: LayerProps[]; setLegendFilter?: SetLegendFilter }) => {
  const { t } = useTranslation();
  const { setOpen } = usePicker();

  const controlsForm = useControlsContext();
  const layer = controlsForm.watch('layer');
  const defaultLayerOpacity =
    DEFAULT_LAYER_OPACITIES[layer as keyof typeof DEFAULT_LAYER_OPACITIES] ?? DEFAULT_LAYER_OPACITY;

  const selectedLabel = LAYER_OPTIONS.find((opt) => opt.value === layer)?.value as MAP_LAYER_FACTS;
  const selectedLayerLabel = selectedLabel
    ? t(`shared.projects.project.heatMapLayers.${selectedLabel}.title`)
    : t('shared.projects.project.conservation.interactiveMap.labels.indicatorLayers');

  const PICKER_SLOT_CONFIG: WidgetSlotConfiguration = {
    name: 'default_heat_map_layer_picker',
    type: WidgetType.o_map_layer,
    widgets: [
      {
        name: 'heat_map_layer_picker',
        type: WidgetType.o_form_picker,
        visible: true,
        relatedData: {},
        widgets: [
          {
            name: 'heat_map_layer_picker_trigger',
            type: WidgetType.o_form_picker_trigger,
            visible: layers.length > 0,
            relatedData: {
              leftAdornment: <RiMap2Line />,
              label: <span>{selectedLayerLabel}</span>,
            },
          },
          {
            name: 'heat_map_layer_picker_content',
            type: WidgetType.o_form_picker_content,
            visible: true,
            relatedData: {},
            widgets: [
              {
                name: 'heat_map_layer_radio_group',
                type: WidgetType.o_form_radio_group,
                visible: true,
                relatedData: {
                  name: 'layer',
                  onValueChange: () => {
                    controlsForm.setValue('layer', layer);
                    if (setLegendFilter) {
                      setLegendFilter(DEFAULT_LEGEND_FILTER);
                    }
                    controlsForm.setValue('habitatIntactnessThreats', []);
                    controlsForm.setValue('layerOpacity', defaultLayerOpacity);
                    setOpen(false);
                  },
                },
                widgets: layers.map(
                  (opt): WidgetSlot => ({
                    name: `${opt.value}_radio_item`,
                    type: WidgetType.a_text,
                    relatedData: {
                      className: 'flex flex-row',
                    },
                    visible: true,
                    widgets: [
                      {
                        name: opt.value,
                        type: WidgetType.o_form_radio_item,
                        visible: true,
                        relatedData: {
                          key: opt.value,
                          label: t(`shared.projects.project.heatMapLayers.${opt.value as MAP_LAYER_FACTS}.title`),
                          value: opt.value,
                        },
                      },
                      {
                        name: 'map_layer_tooltip',
                        type: WidgetType.m_tooltip,
                        visible: true,
                        relatedData: {},
                        widgets: [
                          {
                            name: 'map_layer_tooltip_trigger',
                            type: WidgetType.m_tooltip_trigger,
                            visible: true,
                            widgets: [
                              {
                                name: 'map_layer_tooltip_icon',
                                type: WidgetType.a_icon,
                                visible: true,
                                relatedData: {
                                  icon: 'ri-information-line',
                                  className: 'ml-1 text-active-54 typography-body1',
                                },
                              },
                            ],
                          },
                          {
                            name: 'map_layer_header_tooltip_content',
                            type: WidgetType.m_tooltip_content,
                            visible: true,
                            widgets: [
                              {
                                name: 'map_layer_header_tooltip_text',
                                type: WidgetType.a_text,
                                visible: true,
                                relatedData: {
                                  i18nPath: `shared.projects.project.heatMapLayers.${opt.value}.tooltip.body`,
                                },
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  }),
                ),
              },
            ],
          },
        ],
      },
    ],
  };

  return <WidgetSlotRenderer nestedWidget={PICKER_SLOT_CONFIG} />;
};

const MapMenuItems = ({ layers, setLegendFilter }: { layers: LayerProps[]; setLegendFilter: SetLegendFilter }) => {
  const pathSet = usePathSet();
  const isPlotPage = useMatchCurrentPage([pathSet.plot, pathSet.plotLandMonitoring, pathSet.plotOverview]);
  const selectedLayer = useMapSelectedLayer();

  return (
    <Stack direction='row' spacing={2} className='absolute left-6 top-6'>
      {isPlotPage ? (
        <>
          <PlotViewLayerPicker layers={layers} setLegendFilter={setLegendFilter} />
          <HabitatIntactnessThreatLayersPicker />
        </>
      ) : (
        <GeneralViewLayerPicker layers={layers} setLegendFilter={setLegendFilter} />
      )}
      {selectedLayer && selectedLayer?.value !== MAP_LAYER_FACTS.unknown && <LayerEditMenu />}
      <YearPicker />
    </Stack>
  );
};

export const HeatMapLayer = ({
  setMapStyle,
  setShowPolygonMask,
  layers = LAYER_OPTIONS,
}: {
  setMapStyle: (style: string) => void;
  setShowPolygonMask: (show: boolean) => void;
  layers?: LayerProps[];
}) => {
  const { legendFilter, setLegendFilter } = useLegendFilter();

  const selectedLayer = useMapSelectedLayer();
  const controlsForm = useControlsContext();

  const layer = selectedLayer?.value ?? MAP_LAYER_FACTS.unknown;

  const threatLayers = controlsForm.watch('habitatIntactnessThreats');

  const shouldRenderThreatLayers =
    threatLayers.length > 0 && selectedLayer?.value === MAP_LAYER_FACTS.habitat_intactness_raster_urls;

  useEffect(() => {
    setMapStyle(selectedLayer && !selectedLayer.hasSatelliteBackground ? LIGHT_MAP_STYLE : DEFAULT_MAP_STYLE);
  }, [selectedLayer, setMapStyle]);

  useEffect(() => {
    setShowPolygonMask(!LANDCOVER_VIEW_LAYERS.includes(layer as MAP_LAYER_FACTS));
  }, [layer, setShowPolygonMask]);

  if (selectedLayer?.value === MAP_LAYER_FACTS.unknown) {
    return <MapMenuItems layers={layers} setLegendFilter={setLegendFilter} />;
  }

  return (
    <>
      <MapMenuItems layers={layers} setLegendFilter={setLegendFilter} />
      {shouldRenderThreatLayers ? (
        <>
          <HabitatIntactnessThreatLayers threatLayers={threatLayers} />
          <ThreatLayersLegend />
        </>
      ) : (
        <>
          <MapSourceLayer legendFilter={legendFilter.filter} />
          <MapLegend setLegendFilter={setLegendFilter} legendFilter={legendFilter} />
        </>
      )}
    </>
  );
};

const MapSourceLayer = withSuspenseBoundary(({ legendFilter }: { legendFilter: string }) => {
  const selectedLayer = useMapSelectedLayer();

  switch (selectedLayer?.type) {
    case MAP_TYPE.raster:
      return <RasterSourceLayer layer={selectedLayer} legendFilter={legendFilter} />;
    case MAP_TYPE.geojson:
      return <GeojsonSourceLayer layer={selectedLayer} legendFilter={legendFilter} />;
    default:
      return null;
  }
}, null);

const MapLegend = ({
  setLegendFilter,
  legendFilter,
}: {
  setLegendFilter: SetLegendFilter;
  legendFilter: LegendFilter;
}) => {
  const controlsForm = useControlsContext();
  const layer = controlsForm.watch('layer');
  const isSiemensOrganization = useIsSiemensOrganization();

  const selectedLayer = LAYER_OPTIONS.find((opt) => opt.value === layer);

  if (!selectedLayer?.legends || selectedLayer.legends.length === 0) {
    return null;
  }

  const selectedLayerLabel = selectedLayer?.value as MAP_LAYER_FACTS;

  const legends = [...selectedLayer.legends];
  if (layer === MAP_LAYER_FACTS.combined_ibat_lulc_urls) {
    // TODO: BIG HACK - MVP-7252 - remove once layer is applicable to all organizations
    // Handling this here and not in constants.ts since this logic relies on the organization
    const protectedAreasLegend = isSiemensOrganization
      ? {
          ...IBAT_LEGEND,
          values: [
            {
              text: 'shared.ncaDetail.details.protectedAreas.labels.protectedAreas',
              color: '#FFFF00',
            },
          ],
          hasLegendFilter: false,
        }
      : { ...IBAT_LEGEND, hasLegendFilter: false };
    legends.push(protectedAreasLegend);
  }

  return (
    <Legend>
      <Stack spacing={1}>
        {legends.map((legend, index) => (
          <div key={index}>
            {legend.type === LEGEND_TYPE.numeric && (
              <IndexLegend layerLabel={legend?.layerLabel ?? selectedLayerLabel} legend={legend} />
            )}
            {legend.type === LEGEND_TYPE.category && (
              <CategoryLegend
                layerLabel={legend?.layerLabel ?? selectedLayerLabel}
                legend={legend}
                setLegendFilter={setLegendFilter}
                legendFilter={legendFilter}
              />
            )}
          </div>
        ))}
      </Stack>
    </Legend>
  );
};

const ThreatLayersLegend = () => {
  const { t } = useTranslation();

  const controlsForm = useControlsContext();
  const selectedThreatLayers = controlsForm.watch('habitatIntactnessThreats');

  const threatLayers = HABITAT_INTACTNESS_THREAT_LAYER_OPTIONS.filter(
    (layer) => layer?.layerSubType && selectedThreatLayers.includes(layer.layerSubType as ThreatType),
  );

  if (threatLayers.length === 0) {
    return null;
  }

  const legends = threatLayers
    .map((layer) => layer.legends)
    .flat()
    .filter(Boolean) as LegendConfig[];

  return (
    <Legend>
      <LegendContainer title={t(`shared.projects.project.heatMapLayers.habitat_intactness_raster_urls.title`)}>
        {legends.map((legend) => {
          return legend.type === LEGEND_TYPE.category && legend.values[0] ? (
            <div key={legend.values[0].text}>
              <LegendItem legendItem={legend.values[0]} />
            </div>
          ) : null;
        })}
      </LegendContainer>
    </Legend>
  );
};
